import {
  AudienceType,
  CampaignConfig,
  LeadPriority,
  LeadRecord,
  LeadScoreResult,
  LeadStage,
  LeadSubmissionInput,
  PainPoint,
  ProgramFormat,
} from '../types/leadEngine';
import { supabase } from '../lib/supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const LEADS_TABLE = process.env.REACT_APP_SUPABASE_LEADS_TABLE || 'lead_submissions';
const ADMIN_ENDPOINT = process.env.REACT_APP_LEAD_ADMIN_ENDPOINT;
const FORMSPREE_BACKUP_URL = process.env.REACT_APP_FORMSPREE_URL || 'https://formspree.io/f/mrbkljag';

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn(
    '⚠️ CloudYatra Lead Engine: Supabase variables are not configured. Lead submissions will use the configured Formspree backup endpoint.'
  );
}

export const audienceLabels: Record<AudienceType, string> = {
  student: 'Student',
  researcher: 'Researcher',
  industry_professional: 'Industry professional',
  founder: 'Founder / business owner',
  team_lead: 'Team lead / manager',
  other: 'Other',
};

export const painPointLabels: Record<PainPoint, string> = {
  high_cloud_cost: 'High cloud cost',
  devops_bottleneck: 'DevOps bottleneck',
  migration_planning: 'Migration planning',
  security_compliance: 'Security / compliance',
  data_ai: 'Data / AI use case',
  career_transition: 'Career transition',
  team_training: 'Team training',
  research_collaboration: 'Research collaboration',
  other: 'Other',
};

export const formatLabels: Record<ProgramFormat, string> = {
  online: 'Online',
  offline: 'Offline',
  hybrid: 'Hybrid',
  not_sure: 'Not sure yet',
};

export const stageLabels: Record<LeadStage, string> = {
  new: 'New',
  qualified: 'Qualified',
  nurture: 'Nurture',
  contacted: 'Contacted',
  booked: 'Booked',
  won: 'Won',
  lost: 'Lost',
};

export const campaignCatalog: CampaignConfig[] = [
  {
    slug: 'digital-growth-strategy',
    title: 'Digital Growth Strategy Consultation',
    topic: 'SEO, performance marketing, social media, and AI-powered growth strategy',
    format: 'online',
    offerTitle: 'Get a Free Growth Strategy Call',
    offerDescription: 'Share your goals and receive a tailored digital growth roadmap from a CloudYatra strategist.',
    assetLabel: 'Growth strategy roadmap',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra Digital Growth Solutions',
  },
  {
    slug: 'seo-growth-audit',
    title: 'SEO Growth Audit',
    topic: 'Technical SEO, keyword strategy, content, and organic growth',
    format: 'online',
    offerTitle: 'Get Your Free SEO Audit',
    offerDescription: 'Receive a prioritized view of the technical, content, and visibility opportunities on your website.',
    assetLabel: 'SEO growth audit',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra Digital Growth Solutions',
  },
  {
    slug: 'performance-marketing-audit',
    title: 'Performance Marketing Audit',
    topic: 'Paid acquisition, conversion tracking, and campaign efficiency',
    format: 'online',
    offerTitle: 'Get Your Free Performance Marketing Audit',
    offerDescription: 'Receive a review of your paid acquisition opportunities, tracking gaps, and growth priorities.',
    assetLabel: 'Performance marketing audit',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra Digital Growth Solutions',
  },
  {
    slug: 'cloud-cost-masterclass',
    title: 'Cloud Cost Optimization Masterclass',
    topic: 'FinOps and cloud cost reduction',
    format: 'hybrid',
    offerTitle: 'Get the Cloud Cost Leak Checklist',
    offerDescription: 'A practical checklist to identify idle spend, rightsizing gaps, and governance leaks before the next billing cycle.',
    assetLabel: 'Cloud cost checklist',
    sourceChannel: 'masterclass',
    locationLabel: 'Online and offline cohorts',
  },
  {
    slug: 'devops-cicd-workshop',
    title: 'CI/CD and DevOps Workshop',
    topic: 'Release automation, GitOps, and pipeline governance',
    format: 'offline',
    offerTitle: 'Get the Pipeline Readiness Scorecard',
    offerDescription: 'Assess release frequency, rollback safety, environment drift, and deployment ownership in one page.',
    assetLabel: 'DevOps readiness scorecard',
    sourceChannel: 'workshop',
    locationLabel: 'Classroom workshop',
  },
  {
    slug: 'zero-trust-security-webinar',
    title: 'Zero-Trust Security Webinar',
    topic: 'Cloud security, compliance, and access controls',
    format: 'online',
    offerTitle: 'Get the Zero-Trust Architecture Checklist',
    offerDescription: 'Map identity, workload, network, and monitoring controls into a usable implementation checklist.',
    assetLabel: 'Zero-trust checklist',
    sourceChannel: 'webinar',
    locationLabel: 'Live webinar',
  },
  {
    slug: 'enterprise-learning',
    title: 'Enterprise Learning Consultation',
    topic: 'Cloud, DevOps, cybersecurity, data, and AI training',
    format: 'hybrid',
    offerTitle: 'Get a Tailored Training Plan',
    offerDescription: 'Share your audience and goals so CloudYatra can recommend the right class, workshop, or team learning track.',
    assetLabel: 'Training plan',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra website',
  },
  {
    slug: 'cloud-migration-calculator',
    title: 'Cloud Migration Cost Calculator',
    topic: 'Cloud migration cost estimation and readiness planning',
    format: 'online',
    offerTitle: 'Get Migration Estimate Report',
    offerDescription: 'Complete details of your custom cloud infrastructure and migration costs.',
    assetLabel: 'Cloud migration estimate PDF',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra website',
  },
  {
    slug: 'cloud-cost-audit',
    title: 'Free Cloud Cost Assessment',
    topic: 'Cloud cost governance and FinOps review',
    format: 'online',
    offerTitle: 'Get Free Cost Assessment',
    offerDescription: 'Request a free review of your cloud bill to spot waste and save 20-40%.',
    assetLabel: 'FinOps cost assessment roadmap',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra website',
  },
  {
    slug: 'website-analysis',
    title: 'Free Website Analysis',
    topic: 'Technical health, SEO, UX, and conversion opportunity review',
    format: 'online',
    offerTitle: 'Get Your Free Website Analysis',
    offerDescription: 'Receive a prioritized review of your website, visibility gaps, and growth opportunities.',
    assetLabel: 'Website analysis roadmap',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra Digital Growth Solutions',
  },
  {
    slug: 'content-marketing-audit',
    title: 'Content Marketing Strategy Audit',
    topic: 'Content strategy, SEO content, and distribution',
    format: 'online',
    offerTitle: 'Get Your Free Content Strategy Audit',
    offerDescription: 'Receive a prioritized view of your content, keyword, and audience opportunities.',
    assetLabel: 'Content strategy audit',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra Digital Growth Solutions',
  },
  {
    slug: 'social-media-audit',
    title: 'Social Media Marketing Audit',
    topic: 'Social strategy, creative, community, and paid campaigns',
    format: 'online',
    offerTitle: 'Get Your Free Social Media Audit',
    offerDescription: 'Receive a review of your social presence, engagement, and paid growth opportunities.',
    assetLabel: 'Social media audit',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra Digital Growth Solutions',
  },
  {
    slug: 'orm-audit',
    title: 'Online Reputation Management Audit',
    topic: 'Reviews, ratings, sentiment, and brand trust',
    format: 'online',
    offerTitle: 'Get Your Free ORM Audit',
    offerDescription: 'Receive a review of your reviews, ratings, sentiment, and reputation opportunities.',
    assetLabel: 'ORM audit',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra Digital Growth Solutions',
  },
  {
    slug: 'ai-search-audit',
    title: 'AI Search Visibility Audit',
    topic: 'GEO, AI overviews, and answer engine visibility',
    format: 'online',
    offerTitle: 'Get Your Free AI Search Audit',
    offerDescription: 'Receive a review of how discoverable your brand is in ChatGPT, Gemini, and AI search.',
    assetLabel: 'AI search audit',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra Digital Growth Solutions',
  },
  {
    slug: 'contact-form',
    title: 'General Contact / Assessment Inquiry',
    topic: 'Cloud and security general query',
    format: 'online',
    offerTitle: 'Book Free Assessment',
    offerDescription: 'Share your cloud, server, website, security, cost, or support requirement.',
    assetLabel: 'Assessment inquiry',
    sourceChannel: 'website',
    locationLabel: 'CloudYatra website',
  },
];

export const getCampaignBySlug = (slug?: string): CampaignConfig => {
  return campaignCatalog.find((campaign) => campaign.slug === slug) || campaignCatalog[0];
};

export const calculateLeadScore = (lead: LeadSubmissionInput): LeadScoreResult => {
  let score = 20;

  const emailDomain = lead.email.split('@')[1]?.toLowerCase() || '';
  const isPersonalEmail = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com'].includes(emailDomain);

  if (!isPersonalEmail) score += 20;
  if (lead.company?.trim()) score += 10;
  if (lead.phone?.trim()) score += 10;
  if (lead.linkedinUrl?.trim()) score += 10;

  if (lead.audienceType === 'founder' || lead.audienceType === 'team_lead') score += 25;
  if (lead.audienceType === 'industry_professional') score += 15;
  if (lead.audienceType === 'researcher') score += 8;

  if (lead.painPoint === 'security_compliance' || lead.painPoint === 'high_cloud_cost') score += 20;
  if (lead.painPoint === 'team_training' || lead.painPoint === 'migration_planning') score += 18;
  if (lead.painPoint === 'devops_bottleneck' || lead.painPoint === 'data_ai') score += 14;

  if (lead.programFormat === 'offline' || lead.programFormat === 'hybrid') score += 8;
  if (lead.notes && lead.notes.trim().length > 40) score += 8;

  const priority: LeadPriority = score >= 80 ? 'hot' : score >= 55 ? 'warm' : 'cold';

  return {
    score: Math.min(score, 100),
    priority,
    followUpSegment: getFollowUpSegment(lead.painPoint),
    nextAction: getNextAction(priority, lead),
  };
};

const getFollowUpSegment = (painPoint: PainPoint): string => {
  const segments: Record<PainPoint, string> = {
    high_cloud_cost: 'FinOps audit nurture',
    devops_bottleneck: 'DevOps maturity nurture',
    migration_planning: 'Migration readiness nurture',
    security_compliance: 'Security and compliance nurture',
    data_ai: 'Data and AI enablement nurture',
    career_transition: 'Career upskilling nurture',
    team_training: 'Enterprise training nurture',
    research_collaboration: 'Research partnership nurture',
    other: 'General cloud advisory nurture',
  };

  return segments[painPoint];
};

const getNextAction = (priority: LeadPriority, lead: LeadSubmissionInput): string => {
  if (priority === 'hot') return 'Notify sales and request a same-day manual follow-up.';
  if (lead.audienceType === 'student' || lead.audienceType === 'researcher') return 'Send learning path, class resources, and invite to the next cohort.';
  if (priority === 'warm') return 'Send relevant case study and invite to book a discovery call.';
  return 'Add to nurture sequence and track future engagement.';
};

const createId = (): string => {
  if ('crypto' in window && 'randomUUID' in window.crypto) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const buildRecord = (input: LeadSubmissionInput): LeadRecord => {
  const score = calculateLeadScore(input);
  const now = new Date().toISOString();

  return {
    ...input,
    id: createId(),
    createdAt: now,
    updatedAt: now,
    score: score.score,
    priority: score.priority,
    stage: score.priority === 'cold' ? 'nurture' : 'new',
    followUpSegment: score.followUpSegment,
    nextAction: score.nextAction,
  };
};

const isSupabaseConfigured = (): boolean => Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

const toSupabaseRow = (record: LeadRecord) => ({
  id: record.id,
  created_at: record.createdAt,
  updated_at: record.updatedAt,
  campaign_slug: record.campaignSlug,
  campaign_title: record.campaignTitle,
  source_channel: record.sourceChannel,
  source_url: record.sourceUrl,
  first_name: record.firstName,
  last_name: record.lastName,
  email: record.email,
  phone: record.phone || null,
  company: record.company || null,
  role: record.role || null,
  linkedin_url: record.linkedinUrl || null,
  audience_type: record.audienceType,
  pain_point: record.painPoint,
  program_format: record.programFormat,
  program_interest: record.programInterest,
  notes: record.notes || null,
  consent: record.consent,
  utm_source: record.utmSource || null,
  utm_medium: record.utmMedium || null,
  utm_campaign: record.utmCampaign || null,
  score: record.score,
  priority: record.priority,
  stage: record.stage,
  follow_up_segment: record.followUpSegment,
  next_action: record.nextAction,
  assigned_to: record.assignedTo || null,
  crm_record_id: record.crmRecordId || null,
  last_contacted_at: record.lastContactedAt || null,
});

const fromSupabaseRow = (row: any): LeadRecord => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  campaignSlug: row.campaign_slug,
  campaignTitle: row.campaign_title,
  sourceChannel: row.source_channel,
  sourceUrl: row.source_url,
  firstName: row.first_name,
  lastName: row.last_name || '',
  email: row.email,
  phone: row.phone || '',
  company: row.company || '',
  role: row.role || '',
  linkedinUrl: row.linkedin_url || '',
  audienceType: row.audience_type,
  painPoint: row.pain_point,
  programFormat: row.program_format,
  programInterest: row.program_interest,
  notes: row.notes || '',
  consent: Boolean(row.consent),
  utmSource: row.utm_source || '',
  utmMedium: row.utm_medium || '',
  utmCampaign: row.utm_campaign || '',
  score: Number(row.score || 0),
  priority: row.priority,
  stage: row.stage,
  followUpSegment: row.follow_up_segment,
  nextAction: row.next_action,
  assignedTo: row.assigned_to || '',
  crmRecordId: row.crm_record_id || '',
  lastContactedAt: row.last_contacted_at || '',
});

const supabaseHeaders = {
  apikey: SUPABASE_ANON_KEY || '',
  Authorization: `Bearer ${SUPABASE_ANON_KEY || ''}`,
  'Content-Type': 'application/json',
  Prefer: 'return=representation',
};

const submitToFormspreeFallback = async (record: LeadRecord): Promise<void> => {
  if (!FORMSPREE_BACKUP_URL) {
    throw new Error('Lead capture is not configured. Please contact sales@cloudyatra.co.in.');
  }

  const response = await fetch(FORMSPREE_BACKUP_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      subject: `CloudYatra Web Lead: ${record.campaignTitle}`,
      name: `${record.firstName} ${record.lastName}`.trim(),
      email: record.email,
      phone: record.phone || 'N/A',
      company: record.company || 'N/A',
      role: record.role || 'N/A',
      linkedin: record.linkedinUrl || 'N/A',
      audience: record.audienceType,
      interest: record.programInterest,
      segment: record.followUpSegment,
      score: record.score,
      priority: record.priority,
      context: record.notes || 'N/A',
      source: record.sourceUrl,
    }),
  });

  if (!response.ok) {
    throw new Error('Lead capture failed. Please try again or contact sales@cloudyatra.co.in.');
  }
};

export const leadEngineAPI = {
  isConfigured: isSupabaseConfigured,
  isAdminConfigured: (): boolean => Boolean(ADMIN_ENDPOINT),

  submitLead: async (input: LeadSubmissionInput): Promise<LeadRecord> => {
    const record = buildRecord(input);

    if (isSupabaseConfigured()) {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/${LEADS_TABLE}?select=*`, {
        method: 'POST',
        headers: supabaseHeaders,
        body: JSON.stringify(toSupabaseRow(record)),
      });

      if (!response.ok) {
        throw new Error('Lead capture failed. Please try again.');
      }

      const rows = await response.json();
      const savedRecord = fromSupabaseRow(rows[0]);
      return savedRecord;
    }

    await submitToFormspreeFallback(record);
    return record;
  },

  listLeads: async (): Promise<LeadRecord[]> => {
    if (supabase) {
      const { data, error } = await supabase.from('lead_submissions').select('*').order('created_at', { ascending: false }).limit(200);
      if (error) throw new Error(error.message);
      return (data ?? []).map(fromSupabaseRow);
    }
    if (ADMIN_ENDPOINT) {
      const response = await fetch(ADMIN_ENDPOINT, {
        headers: buildAdminHeaders(),
      });

      if (!response.ok) {
        throw new Error('Unable to load leads from the admin endpoint.');
      }

      const rows = await response.json();
      return rows.map(fromSupabaseRow);
    }

    return [];
  },

  updateLeadStage: async (id: string, stage: LeadStage): Promise<void> => {
    if (supabase) {
      const { error } = await supabase.from('lead_submissions').update({ stage }).eq('id', id);
      if (error) throw new Error(error.message);
      return;
    }
    if (ADMIN_ENDPOINT) {
      const response = await fetch(ADMIN_ENDPOINT, {
        method: 'PATCH',
        headers: buildAdminHeaders(),
        body: JSON.stringify({ id, stage }),
      });

      if (!response.ok) {
        throw new Error('Unable to update lead stage.');
      }

      return;
    }

    throw new Error('Lead administration is not configured.');
  },
};

const buildAdminHeaders = (): HeadersInit => ({ 'Content-Type': 'application/json' });
